import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../config/axiosInstance';
import { getRoleLabel } from '../../config/routePermissions';
import { groupPermissionsForAdmin } from '../../config/permissionLabels';

export default function RolePermissionsAdminPage() {
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [grantedByRole, setGrantedByRole] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingCell, setSavingCell] = useState(null); // `${roleId}:${permissionId}`

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/roles/permissions-matrix');
      const editableRoles = (data?.roles || []).filter((role) => role.editable);

      setRoles(editableRoles);
      setGroups(groupPermissionsForAdmin(data?.permissions || []));

      const byRole = {};
      editableRoles.forEach((role) => {
        byRole[role.id] = new Set(role.permission_ids || []);
      });
      setGrantedByRole(byRole);
    } catch (loadError) {
      setError(loadError?.response?.data?.error || 'No se pudo cargar la matriz de permisos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const hasPermission = (roleId, permissionId) => grantedByRole[roleId]?.has(permissionId) || false;

  const handleToggle = async (roleId, permissionId, nextEnabled) => {
    const cellKey = `${roleId}:${permissionId}`;
    setError('');

    setGrantedByRole((prev) => {
      const next = { ...prev, [roleId]: new Set(prev[roleId]) };
      if (nextEnabled) next[roleId].add(permissionId);
      else next[roleId].delete(permissionId);
      return next;
    });

    setSavingCell(cellKey);
    try {
      await axiosInstance.put(`/roles/${roleId}/permissions/${permissionId}?enabled=${nextEnabled}`);
    } catch (saveError) {
      // revertimos el toggle optimista si el backend lo rechazó
      setGrantedByRole((prev) => {
        const next = { ...prev, [roleId]: new Set(prev[roleId]) };
        if (nextEnabled) next[roleId].delete(permissionId);
        else next[roleId].add(permissionId);
        return next;
      });
      setError(saveError?.response?.data?.error || 'No se pudo guardar el cambio de permiso.');
    } finally {
      setSavingCell(null);
    }
  };

  const roleColumns = useMemo(
    () => roles.map((role) => ({ ...role, label: getRoleLabel(role.name) })),
    [roles]
  );

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Gestión de permisos
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        Habilitá o deshabilitá qué puede hacer cada rol. Los cambios se guardan al instante.
      </Typography>
      <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 3 }}>
        El rol SuperAdmin no aparece acá: tiene acceso total a todas las funciones del sistema y no se edita.
      </Alert>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer sx={{ maxHeight: 700 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 320 }}>Permiso</TableCell>
                {roleColumns.map((role) => (
                  <TableCell key={role.id} align="center" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                    {role.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(({ group, items }) => (
                <React.Fragment key={group}>
                  <TableRow>
                    <TableCell
                      colSpan={roleColumns.length + 1}
                      sx={{ bgcolor: 'action.hover', fontWeight: 700, py: 0.75 }}
                    >
                      {group}
                    </TableCell>
                  </TableRow>
                  {items.map((permission) => (
                    <TableRow key={permission.id} hover>
                      <TableCell>
                        <Tooltip title={permission.name} placement="right">
                          <span>{permission.label}</span>
                        </Tooltip>
                      </TableCell>
                      {roleColumns.map((role) => {
                        const cellKey = `${role.id}:${permission.id}`;
                        return (
                          <TableCell key={role.id} align="center">
                            <Checkbox
                              size="small"
                              checked={hasPermission(role.id, permission.id)}
                              disabled={savingCell === cellKey}
                              onChange={(event) => handleToggle(role.id, permission.id, event.target.checked)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
